import React, { useEffect, useRef, useState } from 'react'
import { useCreateSubscriptionMutation, useVerifyOrderMutation } from './apiSlice';
import { LPLogoWName } from '@assets/index';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@Constants/routes';
import { displayToast } from '@Utils/toast';
import { RootState, useAppSelector } from '@Store/store';
import { CheckIcon } from '@heroicons/react/24/outline';
import styles from './styles';

import { Confetti } from "../../constants/common";
import PaymentSummaryCard from './components/BillingDetails';
import BillingForm from './components/BillingForm';
import { ConfettiWrapper } from '@Utils/ConfettiPortal';
import { useLazyGetProfileQuery } from '@Pages/Profile/apiSlice';
import { AnalyticsParamBuilder, getCookie } from '@Utils/common';
import posthog from 'posthog-js';
var CryptoJS = require("crypto-js");

declare global {
  interface Window {
    Razorpay: any; // Replace `any` with the proper type if available
  }
}

interface PricingPlan {
  monthly: {
    amount: number;
    payable_amount: number;
    discounted_amount: number;
    currency: string;
  };
  annual: {
    amount: number;
    payable_amount: number;
    discounted_amount: number;
    currency: string;
  };
}
type PricingData = Record<string, PricingPlan>;


const Pricing: PricingData = {
  'Other': {
    monthly: {
      amount: 19.99,
      payable_amount: 19.99,
      discounted_amount: 9.99,
      currency: '$'
    },
    annual: {
      amount: 19.99,
      payable_amount: 95.88,
      discounted_amount: 7.99,
      currency: '$'
    }
  },
  'India': {
    monthly: {
      amount: 999,
      payable_amount: 799,
      discounted_amount: 799,
      currency: '₹'
    },
    annual: {
      amount: 999,
      payable_amount: 4788,
      discounted_amount: 399,
      currency: '₹'
    }
  }
}

const COMPONENT = "payment_plan";

interface PaymentOptionProp {
  signupFlow?: boolean
  closeModal?: () => void;
}
const PaymentOptions = ({ signupFlow = true, closeModal = () => { } }) => {


  const [getProfile, { data: getProfileDataResponse }] = useLazyGetProfileQuery();

  const navigate = useNavigate();
  const { user } = useAppSelector((state: RootState) => state.profile);

  const [isExploding, setIsExploding] = useState(false);

  const [billing, setBilling] = useState({});

  const [verifiedPayment, setVerifiedPayment] = useState(false)
  const [countdown, setCountdown] = useState(3);


  const [selectedPlan, setSelectedPlan] = useState<string>('annual');
  const [Country, setCountry] = useState<string>("Other");

  const [BillingDetails, setBillingDetails] = useState(false)

  const [SubscriptionData, setSubscriptionData] = useState<any>(null)

  const [createSubscription, { data: createSubscriptionData, error: createSubscriptionErr, isLoading: createSubscriptionLoading }] = useCreateSubscriptionMutation();
  const [verifyOrder, { data: verifyOrderData, error: verifyOrderErr, isLoading: verifyOrderLoading }] = useVerifyOrderMutation();


  useEffect(() => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let country = 'Other'; // Default

    // Check specific cases for the US or India
    if ((timeZone === 'Asia/Kolkata' || timeZone === 'Asia/Calcutta') && user?.phone_number?.startsWith("+91")) {
      setCountry("India")
    }
    posthog.capture("ui_view", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: timeZone,
      classValue: Country,
      genus: signupFlow ? "signup" : "upgrade"
    }));

    onPlanClick("annual", true)



  }, []);




  useEffect(() => {
    if (verifyOrderData) {
      posthog.capture("api_response", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "verify_payment",
        classValue: "success",
        str_value: JSON.stringify(verifyOrderData)
      }));
      if (verifyOrderData?.status) {
        setTimeout(() => {
          setIsExploding(true);
        }, Confetti.DELAY);
        setTimeout(() => {
          setIsExploding(false);
        }, Confetti.DURATION);
        displayToast('success', verifyOrderData?.message || "Payment successful!")
        getProfile({})
        setVerifiedPayment(true)
      } else {
        displayToast('error', verifyOrderData?.message || "Payment Failed!")
      }
    }

    if (verifyOrderErr) {
      posthog.capture("api_response", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "verify_payment",
        classValue: "error",
        str_value: JSON.stringify(verifyOrderErr)
      }));
      displayToast('error', verifyOrderErr?.data?.error?.message || "Something went wrong")
    }
  }, [verifyOrderData, verifyOrderErr])

  useEffect(() => {
    if (createSubscriptionData) {
      posthog.capture("api_response", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "create_subscription",
        classValue: "success",
        str_value: JSON.stringify(createSubscriptionData)
      }));
      setSubscriptionData(createSubscriptionData)
    };
    if (createSubscriptionErr) {
      posthog.capture("api_response", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "create_subscription",
        classValue: "error",
        str_value: JSON.stringify(createSubscriptionErr)
      }));
      displayToast('error', createSubscriptionErr?.data?.error?.message || "Some error occured")
      console.log(createSubscriptionErr);
      setBillingDetails(false)
    }

  }, [createSubscriptionData, createSubscriptionErr])

  const PayButtonClick = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "pay_button",
      classValue: SubscriptionData?.data.rzp_subscription_id,
      genus: SubscriptionData?.data.amount,
      str_value: JSON.stringify(SubscriptionData?.data),
    }));
    if (SubscriptionData) {
      posthog.capture("rzp_init", AnalyticsParamBuilder({
        kingdom: COMPONENT,
        phylum: "from_pay_button",
        classValue: SubscriptionData?.data.rzp_subscription_id,
        genus: SubscriptionData?.data.amount,
        str_value: JSON.stringify(SubscriptionData?.data),
      }));
      if (!formRef.current?.validateForm()) { displayToast('error', 'Enter the billing details!'); return };

      const options = {
        key: SubscriptionData?.data.rzp_key,
        subscription_id: SubscriptionData?.data.rzp_subscription_id,
        currency: SubscriptionData?.data.currency,
        amount: SubscriptionData?.data.amount,
        name: SubscriptionData?.data.name,
        description: SubscriptionData?.data.description,
        order_id: SubscriptionData?.data.rzp_order_id,
        handler: function (response: any) {
          // usage
          const fbp = getCookie('_fbp');
          const fbc = getCookie('_fbc');
          posthog.capture("rzp_payment_verify", AnalyticsParamBuilder({
            kingdom: COMPONENT,
            phylum: "rzp_handler",
            classValue: SubscriptionData?.data.rzp_subscription_id,
            genus: SubscriptionData?.data.amount,
            str_value: JSON.stringify(response),
          }))
          verifyOrder({
            BillingDetails: billing,
            rzp_sub_id: SubscriptionData?.data.rzp_subscription_id,
            ...response,
            fbp,
            fbc,
            event_source_url: window.location.href,
            rzp_plan_id: SubscriptionData?.data?.rzp_plan_id,
            plan: selectedPlan
          })
        },
        prefill: {
          name: `${user.first_name} ${user.last_name}`,
          email: user.email,
          contact: user.phone,
        },
      };
      const paymentObject = new window.Razorpay(options);
      paymentObject.on('payment.failed', function (response: any) {
        posthog.capture("rzp_payment_failed", AnalyticsParamBuilder({
          kingdom: COMPONENT,
          phylum: "rzp_payment_failed_handler",
          classValue: SubscriptionData?.data.rzp_subscription_id,
          genus: SubscriptionData?.data.amount,
          str_value: JSON.stringify(response),
        }));
        verifyOrder({
          rzp_sub_id: SubscriptionData?.data?.rzp_subscription_id,
          ...response
        })
      });
      paymentObject.open();

    }

  }

  const formRef = useRef<{ validateForm: () => boolean }>(null);



  const handleContinueButton = () => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "continue_button",
    }));
    setBillingDetails(true)
    createSubscription({ plan: selectedPlan, country: Country == "India" ? 'IN' : 'OTH' })
  }


  useEffect(() => {
    if (!verifiedPayment) {
      return
    }
    let seconds = 5;
    const timer = setInterval(() => {
      seconds -= 1;
      setCountdown(seconds);
      if (seconds == 0) {
        console.log("herer")
        if (signupFlow) navigate(ROUTES.DASHBOARD, { replace: true });
        else closeModal();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [verifiedPayment]); // starts countdown even before API finishes

  const onPlanClick = (plan: string, autoSelect: boolean = false) => {
    posthog.capture("ui_action", AnalyticsParamBuilder({
      kingdom: COMPONENT,
      phylum: "select_plan",
      classValue: plan,
      genus: autoSelect ? "auto_select" : "manual_select"
    }));
    setSelectedPlan(plan)
  }



  return (
    <div
      style={styles.CONTAINER}
      className={`bg-gradient-to-b from-white to-blue-50 max-w-[100%]  flex items-center justify-center p-2 md:p-6 ${signupFlow
        ? 'min-h-screen'
        : 'min-h-0'
        }`}
    >
      {isExploding && (

        <div className="absolute z-[9999] pointer-events-none">
          <ConfettiWrapper />
        </div>
      )}
      {verifiedPayment &&
        <div className='w-full pb-5 h-[80vh] md:h-auto flex flex-col justify-center'>
          <img className='mx-auto max-w-[150px] w-[30%]' src='https://img.icons8.com/fluency/500/checked.png' />
          <h2 className='text-center font-bold text-3xl'> Upgraded to Pro</h2>
          <p className='text-center mt-4'>Payment successful and you are now on pro tier of LinkPlease</p>
          <p className='text-center mt-4 text-sm'>Redirecting to dashboard in {countdown}...</p>
        </div>
      }


      {!verifiedPayment && !BillingDetails && <div className={signupFlow ? 'w-full' : 'h-full w-full flex items-center justify-center overflow-y-auto'}>

        <div className="max-w-[100%] w-full rounded-lg pt-0 mx-auto">
          {/* Header */}
          {signupFlow && <div className="flex items-center justify-center mb-6 mb-2">
            <img src={LPLogoWName} className="w-[100px] sm:w-[185px]" alt="Logo" />
          </div>}

          {/* Main two-column layout */}
          <div className="max-w-[800px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column: Features */}
            <div className='border border-2 border-[#ffbe18] rounded-lg p-3 shadow-lg'>
              <div className='flex flex-col justify-center m-3 mb-6'>
                <button className="mx-auto bg-[linear-gradient(265.93deg,#ffc125_-27.12%,#ffd86e,#ffbe18_125.94%)] flex-shrink-0 px-4 py-1 rounded-xl">
                  <img alt="" className="h-4 w-4" src="https://in.imitari.app/zorcha/premium-icon-crown.svg?tr=f-webp,w-600" />
                </button>
                <h1 className='text-center text-[22px] font-medium'>Get LinkPlease PRO</h1></div>
              <div>

                <ul className="text-md text-gray-700 space-y-2">
                  <li>
                    <CheckIcon className="text-green-600 size-6 inline" /> Unlimited Automation
                  </li>
                  <li>
                    <CheckIcon className="text-green-600 size-6 inline" /> Unlimited Contacts
                  </li>
                  <li>
                    <CheckIcon className="text-green-600 size-6 inline" /> Unlimited Automations Triggers
                  </li>
                  <li>
                    <CheckIcon className="text-green-600 size-6 inline" /> Re-Trigger on existing comments
                  </li>
                  <li>
                    <CheckIcon className="text-green-600 size-6 inline" /> Priority Queue
                  </li>
                  <li>
                    <CheckIcon className="text-green-600 size-6 inline" /> Founder Priority Call/WA Support
                  </li>
                </ul>
              </div>
            </div>

            {/* Right column: Plan selection */}
            <div className="flex flex-col">
              {/* <p className="my-2 text-center text-md font-semibold text-[#000] bg-[#ffd86e] px-3 py-1 rounded-full shadow-sm border border-[#6355ff]/20">
                🤑 Early Bird Offer 🤑
              </p> */}
              <div className="flex flex-col gap-4 mb-6">
                {/* Annual Plan */}
                <div
                  className={`relative rounded-2xl p-[6px] cursor-pointer transition bg-[linear-gradient(265.93deg,#ffc125_-27.12%,#ffd86e,#ffbe18_125.94%)] ${selectedPlan === 'annual' ? "" : "opacity-[75%]"}`}
                  onClick={() => onPlanClick('annual')}
                >
                  <div className=" text-md  font-bold text-black text-center py-1 rounded-full shadow-sm">
                      🪔 Diwali Offer 🪔
                    </div>
                  <div className={`w-full rounded-xl px-4 py-3 flex items-center justify-between bg-[#2e006c] text-white `}>
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center `}
                      >
                        {selectedPlan === 'annual' && (
                          <div className="w-3 h-3 rounded-full bg-white"></div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-lg font-semibold">Annual</span>
                        <span className={`mt-1 inline-block text-xs font-medium px-2 py-0.5 rounded  bg-[#ffd86e] text-[#2e006c] `}>
                          Recommended
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-[30px] font-bold">
                          {Pricing[Country].annual.currency}
                          {Pricing[Country].annual.discounted_amount}/mo
                        </p>
                        <p className='gap-2'>
                          <span className={`text-white/70 line-through mr-1`}>
                          {Pricing[Country].annual.currency}
                          {Pricing[Country].annual.amount}/mo
                        </span>
                          <span className={`bg-[#ffd86e] text-black text-xs font-semibold px-2 py-1 rounded`}>
                        {Math.round(
                          (1 -
                            Pricing[Country].annual.discounted_amount /
                            Pricing[Country].annual.amount) *
                          100
                        )}% OFF
                      </span>
                        </p>
                        
                      </div>
                      
                    </div>
                  </div>
                </div>

                {/* Monthly Plan */}
                <div
                  className={`border-[3px] rounded-lg p-2 flex items-center justify-between cursor-pointer transition h-[100px] ${selectedPlan === 'monthly' ? "border-[#6355ff]" : "border-gray-300 opacity-[75%]"
                    }`}
                  onClick={() => onPlanClick('monthly')}
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-3">
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center `}
                      >
                        {selectedPlan === 'monthly' && (
                          <div className="w-3 h-3 rounded-full bg-[#6355ff]"></div>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-lg font-semibold">Monthly</span>
                        <span className="bg-[#dfdcff] text-[#6355ff] text-xs font-medium px-2 py-1 rounded">
                        Save ~
                        {Math.round(
                          (1 -
                            Pricing[Country].monthly.discounted_amount /
                            Pricing[Country].monthly.amount) *
                          100
                        )}
                        %
                      </span>
                      </div>
                    </div>

                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold">
                      {Pricing[Country].monthly.currency}
                      {Pricing[Country].monthly.discounted_amount}/mo
                    </p>
                    <p className="text-gray-500 line-through">
                      {Pricing[Country].monthly.currency}
                      {Pricing[Country].monthly.amount}/mo
                    </p>
                  </div>
                </div>
              </div>

              {/* Continue button */}
              <button
                onClick={handleContinueButton}

                className={`bg-[#6355ff] flex items-center justify-center gap-4 px-4 py-2 rounded-[0.5rem] w-full my-2 disabled:opacity-[.5] text-white h-[50px] text-lg`}
              >
                Continue
              </button>

              {/* Free plan link */}
              {signupFlow && <button
                onClick={() => navigate(ROUTES.DASHBOARD)}
                className="text-center w-full hover:font-medium text-gray-500 text-lg underline"
              >
                Start with Free Plan
              </button>}
            </div>
          </div>

          {/* Footer */}
          {signupFlow && <div className="mt-3 flex flex-col items-center text-[14px]">
            <p className="text-[#9F9F9F]">By signing up, you agree to LinkPlease's</p>
            <p className="text-[#6355ff]">
              <a
                href="https://linkplease.co/terms-and-conditions/"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>{" "}
              <span className="text-[#9F9F9F]">and</span>{" "}
              <a
                href="https://linkplease.co/privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>
            </p>

            <div className="mt-4 text-sm text-gray-500 space-x-4">
              <span className="cursor-pointer hover:underline">Logout</span>
            </div>
          </div>}
        </div>
      </div>}
      {!verifiedPayment && BillingDetails && (
        <div className=''>
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            {/* Left Column (60%) */}
            <div className="w-full md:w-[60%]">
              <BillingForm userData={user} onDataChange={(data) => setBilling(data)} ref={formRef} />
            </div>

            {/* Right Column (40%) */}
            <div className="w-full md:w-[40%]">
              <PaymentSummaryCard
                buttonLoading={verifyOrderLoading || createSubscriptionLoading}
                plan={Pricing[Country]?.[selectedPlan as keyof PricingPlan]}
                planName={selectedPlan}
                onChangeClick={() => setBillingDetails(false)}
                onPay={PayButtonClick}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );

};

// export default PricingPage;

export default PaymentOptions
